import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useJourneyReviewContext } from 'modules/workflows/context/JourneyReviewContext';
import { ButtonType, DEFAULT_PADDING, FlowChart, focusNode } from 'ui/workflows/flowchart';
import noop from 'utils/noop';
import { JourneyAssist } from '../JourneyAssist';
import { TakeoverType } from '../takeovers';
import { DELETE_NODE_MODAL, NODE_TYPES, SELECT_TILESET_MODAL } from '../../constants';
import { useJourneyAssistContext } from '../../context/JourneyAssistContext';
import { useWorkflowContext } from '../../context/WorkflowContext';
import { ExitRuleSlideOver } from '../ExitRuleSlideOver';
import {
  closeTakeover,
  openTakeover,
  addEdges,
  addNodes,
  cloneNodes,
  getCombos,
  getStartNodeId,
  getIsJourneyReviewPanelEnabled,
  openModal,
  openNodeRequest,
  selectNodes,
  updateIsAsyncLoading,
  updateNodes,
} from '../../ducks';
import { useNotesVisibility } from '../../hooks';
import {
  copyLinkToNode,
  parseAddedEdges,
  parseAddedNodes,
  parseClonedNodes,
  VIEW_METRICS_BUTTON_TYPE,
} from '../../utils';
import { NodesCountWarning } from './NodesCountWarning';
import NotesInlineEdit from './NotesInlineEdit';
import { useJourneyPowerTools } from './usePowerModeHook';
import {
  FlowChartChildWrapper,
  ToolbarFlexContainer,
  ToolbarWrapper,
} from './WorkflowFlowchart.styles';
import { isNodeInlineEditEnabled } from './WorkflowFlowchart.utils';
import { BreakdownType } from '../takeovers/JourneyVisitorBreakdownTakeover/JourneyVisitorBreakdown.constant';

const PROPS_WHICH_TRIGGER_RERENDER = [
  'width',
  'height',
  'nodes',
  'edges',
  'isOverlayVisible',
  'isJourneyAssistActive',
  'onNodeDblClick',
  // REVIEW: What is the best way to check equality for a component?
  'children',
  'onButtonClick',
];

const shouldFlowchartUpdate = (prevProps, nextProps) => {
  return PROPS_WHICH_TRIGGER_RERENDER.every((prop) => isEqual(prevProps[prop], nextProps[prop]));
};

const MemoizedFlowchart = React.memo(FlowChart, shouldFlowchartUpdate);

const WorkflowFlowchartCmp = ({
  Sidebar,
  Toolbar,
  canEditNode,
  children,
  edges = [],
  height,
  isDraft,
  isNew,
  isReadOnly,
  nodes = [],
  onLoad = noop,
  onSelectMetricsClick = noop,
  openedNodeId,
  width,
  workflowId,
  ...props
}) => {
  const dispatch = useDispatch();

  const handleTakeoverOpen = useCallback(
    (type, details) => dispatch(openTakeover(type, details)),
    [],
  );

  const { showExitRuleSlideOver } = useWorkflowContext();
  const { shouldShowJourneyAssist, isJourneyAssistActive, onHighlightDialog } =
    useJourneyAssistContext();
  const [, setIsNotesVisible] = useNotesVisibility();
  const graph = useRef(null);
  const shouldShowNodeCountWarningBanner = !isReadOnly && nodes.length > 100;
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [isEditInProgress, setIsEditInProgress] = useState(false);
  const [isFirstLoadComplete, setIsFirstLoadComplete] = useState(false);

  const isJourneyReviewPanelEnabled = useSelector(getIsJourneyReviewPanelEnabled);
  const { setIsOpen: setIsJourneyReviewOpen, isOpen } = useJourneyReviewContext();

  const startNodeId = useSelector(getStartNodeId);

  const combos = useSelector(getCombos);

  const handleOpenNodeVistorTakeover = (overrides = {}) => {
    handleTakeoverOpen(TakeoverType.NodeVisitors, {
      onClose: () => dispatch(closeTakeover(TakeoverType.NodeVisitors)),
      ...overrides,
    });
  };

  /**
   * Focuses on the start node
   * https://iterable.atlassian.net/browse/WF-2844
   * REVIEW: Would we want to keep the nodes position if the user purposely
   * offsets the start node?  If so, how would we figure out intention?
   */
  const focusStartNode = () => {
    if (!graph.current || !nodes?.length || isNil(startNodeId)) return;
    focusNode(graph.current, `${startNodeId}`);
  };

  const handleCloseJourneyReview = () => setIsJourneyReviewOpen(false);

  const handleAddItems = useCallback(({ nodes: addedNodes = [], edges: addedEdges = [] }) => {
    // If sticky note is added, make all sticky notes visible
    if (addedNodes.some(({ data }) => data.type === NODE_TYPES.NOTE)) {
      setIsNotesVisible(true);
    }
    if (addedNodes.some(({ data }) => data.type === NODE_TYPES.TILESET)) {
      dispatch(
        openModal(SELECT_TILESET_MODAL, {
          startX: addedNodes[0]?.data?.x,
          startY: addedNodes[0]?.data?.y,
        }),
      );

      // prevent tileset 'node' from being introduced to Redux
      return;
    }
    if (addedEdges?.length) {
      dispatch(addEdges(parseAddedEdges(addedEdges)));
    }
    if (addedNodes?.length) {
      dispatch(addNodes(parseAddedNodes(addedNodes)));
    }
  });

  const handleCloneItems = useCallback(
    ({ nodes: addedNodes = [] }) => {
      if (addedNodes?.length) {
        dispatch(cloneNodes(parseClonedNodes(addedNodes.map((n) => n.data))));
      }
    },
    [isDraft],
  );

  const handleDelete = useCallback(
    ({ nodes: nodesToDelete = [], edges: edgesToDelete = [] }) =>
      dispatch(
        openModal(DELETE_NODE_MODAL, {
          nodesToDelete: nodesToDelete.map((n) => n.data),
          edgesToDelete: edgesToDelete.map((e) => e.data),
        }),
      ),
    [],
  );

  const handleNodeDblClick = useCallback(
    ({ id, data, childNodeData } = {}) => {
      //  If the node is a note or if Journey Assist is active, do not open the node editor
      if (isNodeInlineEditEnabled(data)) return;

      if (isJourneyAssistActive) {
        onHighlightDialog();
        return;
      }

      // if already open, there is no need to call again
      if (id !== openedNodeId) {
        dispatch(openNodeRequest(id, { childNodeData }));
      }
    },
    [isJourneyAssistActive, onHighlightDialog],
  );

  const handleNodePositionChange = useCallback(
    ({ nodes: updatedNodes = [] }) => dispatch(updateNodes(updatedNodes.map((n) => n.data))),
    [],
  );

  const handleNodeSelect = useCallback(
    ({ nodes: selectedNodes = [] }) => dispatch(selectNodes(selectedNodes.map((n) => n.id))),
    [],
  );

  const handleOnTestInit = useCallback((cmp) => {
    if (process.env.NODE_ENV === 'test' && typeof props.onTestInit === 'function') {
      props.onTestInit(cmp);
    }
  }, []);

  const handleOnLoad = useCallback(
    (g) => {
      graph.current = g;
      focusStartNode();
      onLoad(g);
    },
    [onLoad],
  );

  const handleButtonClick = useCallback(
    ({ id, data, buttonType }) => {
      if (buttonType === ButtonType.NumUsers) {
        handleOpenNodeVistorTakeover({
          nodeData: data,
        });
      } else if (buttonType === ButtonType.NumUsersEntered) {
        if (data.visitorsEnteredCount > 0) {
          dispatch(
            openTakeover(TakeoverType.JourneyVisitorBreakdown, {
              breakdownType: BreakdownType.VisitorsEntered,
              nodeId: id,
              numUserTotal: data.visitorsEnteredCount,
            }),
          );
        }
      } else if (buttonType === ButtonType.NumUsersExited) {
        if (data.visitorsExitedCount > 0) {
          dispatch(
            openTakeover(TakeoverType.JourneyVisitorBreakdown, {
              breakdownType: BreakdownType.VisitorsExited,
              nodeId: id,
              numUserTotal: data.visitorsExitedCount,
            }),
          );
        }
      } else if (buttonType === VIEW_METRICS_BUTTON_TYPE) {
        onSelectMetricsClick();
      } else if (buttonType === ButtonType.Link && data?.type === NODE_TYPES.NOTE) {
        copyLinkToNode({ nodeId: id, workflowId })
          .then(() => dispatch(addPositiveToast('Link copied to clipboard')))
          .catch((err) => dispatch(addNegativeToastWithErrorId(err.message, 'copyLinkToNode')));
      }
    },
    [isDraft],
  );

  useEffect(() => {
    setIsOverlayVisible(isEditInProgress || typeof openedNodeId === 'string');
  }, [openedNodeId, isEditInProgress]);

  useEffect(() => {
    // Only focus on the start node the first time the nodes are loaded to
    // prevent change in focus on adding new nodes and saving draft
    if (nodes?.length && !isFirstLoadComplete) {
      focusStartNode();
      setIsFirstLoadComplete(true);
    }
  }, [nodes]);

  // internally returns {} if NODE_ENV === 'production'
  useJourneyPowerTools();
  return (
    <MemoizedFlowchart
      width={width}
      height={height}
      nodes={nodes}
      edges={edges}
      combos={openedNodeId ? [] : combos}
      /**
       * `fitViewPadding` is the distance of the nodes from the sides after
       * clicking `Zoom to Fit` or focusing on a node.
       * The format is: `[top, right, bottom, left]`.
       *
       * Right Padding: Extra padding if the sidebar is present so that the
       * sidebar doesn't cover the rightmost node.
       *
       * Bottom Padding: Extra padding on bottom so the bottom-most node is
       * still above the toolbar
       */
      graphOptions={{
        fitViewPadding: [DEFAULT_PADDING, Sidebar ? 250 : DEFAULT_PADDING, 80, DEFAULT_PADDING],
      }}
      isJourneyAssistActive={isJourneyAssistActive}
      isReadOnly={isReadOnly}
      isOverlayVisible={isOverlayVisible}
      onAdd={handleAddItems}
      onBeforeRelayout={() => dispatch(updateIsAsyncLoading(true))}
      onAfterRelayout={() => dispatch(updateIsAsyncLoading(false))}
      onButtonClick={handleButtonClick}
      onClone={handleCloneItems}
      onDelete={handleDelete}
      onLoad={handleOnLoad}
      onNodeDblClick={handleNodeDblClick}
      onNodePositionChange={handleNodePositionChange}
      onNodeSelectChange={handleNodeSelect}
      onItemMouseDown={isJourneyReviewPanelEnabled && isOpen ? handleCloseJourneyReview : noop}
      onTestInit={handleOnTestInit}
      {...props}
    >
      <FlowChartChildWrapper>
        <ToolbarFlexContainer>
          {!isOverlayVisible && <ToolbarWrapper>{Toolbar}</ToolbarWrapper>}
        </ToolbarFlexContainer>
        {shouldShowNodeCountWarningBanner && <NodesCountWarning openedNodeId={+openedNodeId} />}
        {!isReadOnly && shouldShowJourneyAssist && <JourneyAssist />}
        {showExitRuleSlideOver && <ExitRuleSlideOver />}
        {!isReadOnly && Sidebar}
      </FlowChartChildWrapper>
      {canEditNode && <NotesInlineEdit onEditStateChange={setIsEditInProgress} />}
    </MemoizedFlowchart>
  );
};

export const WorkflowFlowchart = React.memo(WorkflowFlowchartCmp);

export default WorkflowFlowchart;
